import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { StaleCustomersAlert } from '../components/StaleCustomersAlert';
import { WinLossCard } from '../components/WinLossCard';
import { ContractsAwaitingSignatureWidget } from '../components/ContractsAwaitingSignatureWidget';
import { ContractsAwaitingCountersignWidget } from '../components/ContractsAwaitingCountersignWidget';
import { DnoActionRequiredWidget } from '../components/DnoActionRequiredWidget';
import { DnoRaiseReminderWidget } from '../components/DnoRaiseReminderWidget';
import { DepositsOutstandingWidget } from '../components/DepositsOutstandingWidget';
import { UpcomingBookingsWidget } from '../components/UpcomingBookingsWidget';
import { BookingHealthTile } from '../components/BookingHealthTile';
import { PeriodProfitWidget } from '../components/PeriodProfitWidget';
import { OutstandingInvoicesBar } from '../components/OutstandingInvoicesBar';
import { PaymentSyncWidget } from '../components/PaymentSyncWidget';
import { dashboardAPI, customersAPI } from '../lib/api';
import { formatDate, formatSystemSize, formatCurrency, STATUS_DISPLAY_NAMES, STATUS_COLORS, PROJECT_STAGES } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Users,
  UserPlus,
  Zap,
  CheckCircle,
  TrendingUp,
  ArrowRight,
  Clock,
  Sun,
  Loader2,
  Upload,
  Trash2,
  Mail,
  Edit,
  ClipboardCheck,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const CHART_COLORS = ['#2563eb', '#0ea5e9', '#059669', '#8b5cf6', '#06b6d4', '#f43f5e'];

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [myCustomers, setMyCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, activityRes, myCustomersRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getRecentActivity(),
          dashboardAPI.getMyCustomers().catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setRecentActivity(activityRes.data);
        setMyCustomers(myCustomersRes.data || []);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  const pipelineData = stats?.stage_counts
    ? Object.entries(stats.stage_counts)
        .filter(([_, count]) => count > 0)
        .map(([stage, count]) => ({
          name: STATUS_DISPLAY_NAMES[stage] || stage,
          value: count,
          stage,
        }))
    : [];

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Dashboard</h1>
            <p className="text-slate-500 mt-1">Welcome to the Helios Electrical and Solar App</p>
          </div>
          <Link to="/customers/new">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-customer-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </Link>
        </div>

        {/* 1. Headline: contracts signed + profit, filterable by period */}
        <PeriodProfitWidget />

        {/* 2. Contracts the customer signed — awaiting our counter-signature */}
        <ContractsAwaitingCountersignWidget />

        {/* 3. Contracts awaiting customer signature */}
        <ContractsAwaitingSignatureWidget />

        {/* 4. Signed + deposit paid — DNO application still to raise */}
        <DnoRaiseReminderWidget />

        {/* DNO applications needing our action */}
        <DnoActionRequiredWidget />

        {/* 5. Outstanding invoices — deposits + final payments (running totals) */}
        <OutstandingInvoicesBar />

        {/* 6. Invoice paid-status vs customer record out of sync */}
        <PaymentSyncWidget />

        {/* Signed customers with an unpaid deposit */}
        <DepositsOutstandingWidget />

        {/* Website call bookings → Outlook calendar health */}
        <BookingHealthTile />

        {/* Upcoming call bookings made via the website (today + next 7 days) */}
        <UpcomingBookingsWidget />

        {/* My Customers */}
        {myCustomers.length > 0 && (
          <Card className="border-slate-200" data-testid="my-customers-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  My Customers ({myCustomers.length})
                </CardTitle>
                <Link to="/customers">
                  <Button variant="ghost" size="sm" className="text-xs">
                    View All <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {myCustomers.map(c => {
                  const statusColor = STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-700';
                  return (
                    <Link
                      key={c.id}
                      to={`/customers/${c.id}`}
                      className="block p-3 border border-slate-100 rounded-lg hover:bg-slate-50 hover:border-slate-200 transition-colors"
                      data-testid={`my-customer-${c.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {c.first_name} {c.last_name}
                          </p>
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {c.address}{c.city ? `, ${c.city}` : ''} {c.postcode}
                          </p>
                          {(c.confirmed_install_date || c.provisional_install_date) && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatDate(c.confirmed_install_date || c.provisional_install_date)}
                            </p>
                          )}
                        </div>
                        <Badge className={`${statusColor} text-[10px] ml-2 flex-shrink-0`}>
                          {STATUS_DISPLAY_NAMES[c.status] || c.status}
                        </Badge>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="card-hover border-slate-200" data-testid="stat-total-customers">            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Total Customers</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_customers || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover border-slate-200" data-testid="stat-leads">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Active Leads</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.leads || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover border-slate-200" data-testid="stat-in-progress">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">In Progress</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.in_progress || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover border-slate-200" data-testid="stat-completed">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Completed</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.completed || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Win/Loss Report (this month) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-2">
            <WinLossCard />
          </div>

          {/* Pipeline Chart */}
          <Card className="lg:col-span-2 border-slate-200" data-testid="pipeline-chart">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Manrope']">
                Pipeline Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pipelineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={pipelineData} layout="vertical">
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={120}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {pipelineData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-48 text-slate-400">
                  No data to display
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* System Totals */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Total System Size */}
          <Card className="border-slate-200" data-testid="stat-system-size">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <Sun className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Total System Size</p>
                  <p className="text-2xl font-bold text-slate-900">{formatSystemSize(stats?.total_system_size || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total System Cost */}
          <Card className="border-slate-200" data-testid="stat-system-cost">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                  <span className="text-white text-xl font-bold">£</span>
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Total System Value</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.total_system_cost || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity Timeline */}
        <Card className="border-slate-200" data-testid="recent-activity">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900 font-['Manrope']">
              Recent Activity
            </CardTitle>
            <Link to="/customers">
              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700">
                View all
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentActivity.length > 0 ? (
              <div className="space-y-1">
                {recentActivity.map((activity, index) => {
                  const getIcon = (type) => {
                    switch (type) {
                      case 'status_change': return <ChevronRight className="w-3.5 h-3.5 text-amber-500" />;
                      case 'document_upload': return <Upload className="w-3.5 h-3.5 text-blue-500" />;
                      case 'document_delete': return <Trash2 className="w-3.5 h-3.5 text-red-500" />;
                      case 'email_sent': return <Mail className="w-3.5 h-3.5 text-purple-500" />;
                      case 'customer_update': return <Edit className="w-3.5 h-3.5 text-slate-500" />;
                      case 'checklist_update': return <ClipboardCheck className="w-3.5 h-3.5 text-emerald-500" />;
                      case 'portal_login': return <ExternalLink className="w-3.5 h-3.5 text-cyan-500" />;
                      default: return <Clock className="w-3.5 h-3.5 text-slate-400" />;
                    }
                  };
                  const getBg = (type) => {
                    switch (type) {
                      case 'status_change': return 'bg-amber-50 border-amber-200';
                      case 'document_upload': return 'bg-blue-50 border-blue-200';
                      case 'document_delete': return 'bg-red-50 border-red-200';
                      case 'email_sent': return 'bg-purple-50 border-purple-200';
                      case 'customer_update': return 'bg-slate-50 border-slate-200';
                      case 'checklist_update': return 'bg-emerald-50 border-emerald-200';
                      case 'portal_login': return 'bg-cyan-50 border-cyan-200';
                      default: return 'bg-slate-50 border-slate-200';
                    }
                  };
                  return (
                    <Link
                      key={index}
                      to={`/customers/${activity.customer_id}?tab=history`}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all hover:shadow-sm ${getBg(activity.type)}`}
                      data-testid={`activity-item-${index}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-white border flex items-center justify-center flex-shrink-0">
                        {getIcon(activity.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-slate-900">{activity.customer_name}</span>
                          {activity.type === 'status_change' && activity.status && (
                            <Badge className={`${STATUS_COLORS[activity.status] || 'bg-slate-100'} text-xs`}>
                              {activity.status_display}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 truncate">{activity.description}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-slate-400">{formatDate(activity.timestamp)}</p>
                        <p className="text-xs text-slate-400">{activity.user_name}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Clock className="w-12 h-12 mb-4" />
                <p>No recent activity</p>
                <Link to="/customers/new" className="mt-4">
                  <Button variant="outline" size="sm">
                    Add your first customer
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customers requiring attention — moved to the bottom */}
        <StaleCustomersAlert />
      </div>

      {/* My Customers block moved above - right below header */}
    </Layout>
  );
};
